/**
 * Black Friday 2026: the three offer posters' pages (snippets/bfcm-pdp.liquid).
 *
 * - While the offer edition is chosen (unframed; the Constitution vertical),
 *   the flat print stands in place of the product photos. Choosing another
 *   edition, or tapping a photo thumbnail, shows the photos; the first
 *   thumbnail, or choosing the offer edition again, brings the print back.
 *   Tapping the print opens the theme's photo viewer on that edition's photo.
 * - When the link names no edition, the page opens on the offer edition (the
 *   Constitution's first edition is Framed). A link with ?variant= is kept.
 * - The note in the offer box ("The free poster offer is for the unframed
 *   print") shows only while another edition is chosen.
 * - On wide screens, a softly lit patch of wall sits behind the buying column,
 *   down to the end of the offer box, with room around the text: as wide as
 *   the space beside the print and the edge of the screen allow.
 * - The painting reaches up behind the logo, menu and breadcrumbs to the
 *   Black Friday bar: --bfcm-lift is measured here and kept up to date.
 *
 * Nothing here adds to the cart: the offer box buttons use bfcm-hero.js.
 */
(() => {
  const print = document.querySelector('[data-bfcm-pdp-print]');
  if (!print || print.dataset.bfcmReady) return;
  print.dataset.bfcmReady = 'true';

  const product = print.closest('.js-product');
  const gallery = print.closest('media-gallery');
  const main = gallery && gallery.querySelector('.main-image');
  if (!product || !gallery || !main) return;

  const thumb = gallery.querySelector('.bfcm-pdp-thumb');
  const note = product.querySelector('[data-bfcm-pdp-note]');
  const offerVariant = Number(print.dataset.offerVariant);
  let printShown = !print.hidden;

  // The theme marks the thumbnail of the photo it shows; while the print is
  // shown, the mark belongs on the print's own thumbnail.
  const markThumbnails = () => {
    if (!thumb) return;
    const activeMedia = main.querySelector('.slider__item.is-active');
    gallery.querySelectorAll('.thumbnails .slider__item').forEach((item) => {
      const isActive = printShown
        ? item === thumb
        : Boolean(activeMedia) && item.dataset.mediaId === activeMedia.dataset.mediaId;
      item.classList.toggle('is-active', isActive);
    });
  };

  const showPrint = (on) => {
    printShown = on;
    print.hidden = !on;
    main.toggleAttribute('inert', on);
    markThumbnails();
  };

  main.toggleAttribute('inert', printShown);

  gallery.addEventListener('click', (evt) => {
    if (evt.target.closest('[data-bfcm-pdp-show-print]')) {
      evt.preventDefault();
      showPrint(true);
    } else if (evt.target.closest('[data-bfcm-pdp-zoom]')) {
      evt.preventDefault();
      const link = main.querySelector(`.slider__item[data-media-id="${print.dataset.mediaId}"] a.show-gallery`)
        || main.querySelector('a.show-gallery');
      if (link) {
        // The theme opens its viewer from this link's click.
        main.removeAttribute('inert');
        link.click();
        if (printShown) main.setAttribute('inert', '');
      }
    } else if (printShown && evt.target.closest('.thumbnails .thumbnail')) {
      // A photo thumbnail: show the photos, then the theme shows that photo.
      showPrint(false);
    }
  }, true);

  product.addEventListener('on:variant:change', (evt) => {
    const variant = evt.detail && evt.detail.variant;
    const isOffer = Boolean(variant) && Number(variant.id) === offerVariant;
    if (note) note.hidden = isOffer;
    showPrint(isOffer);
  });

  document.addEventListener('on:media-gallery:change', markThumbnails);

  // Open on the offer edition, the way a buyer would pick it: check its option
  // buttons, then let the theme's picker update price, button and photos.
  const offerOptions = JSON.parse(print.dataset.offerOptions || '[]');
  const picker = product.querySelector('variant-picker');
  if (picker && offerOptions.length && !new URLSearchParams(window.location.search).has('variant')) {
    // The picker's change also updates the gallery and store pickup, so wait
    // until the theme has set those up.
    const parts = ['variant-picker', 'media-gallery'];
    if (product.querySelector('pickup-availability')) parts.push('pickup-availability');
    Promise.all(parts.map((name) => customElements.whenDefined(name))).then(() => {
      if (typeof picker.getSelectedVariant !== 'function') return;
      const selected = picker.getSelectedVariant();
      if (selected && Number(selected.id) === offerVariant) return;
      const selectors = picker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      if (selectors.length !== offerOptions.length) return;
      let changed = null;
      selectors.forEach((selector, index) => {
        const input = [...selector.querySelectorAll('input[type="radio"]')].find((el) => el.value === offerOptions[index]);
        if (input && !input.checked) {
          input.checked = true;
          changed = input;
        }
      });
      if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
  window.addEventListener('load', markThumbnails, { once: true });

  // The painting starts at the top of the header, right under the Black Friday bar.
  const hall = document.querySelector('.bfcm-pdp-hall');
  const header = document.getElementById('pageheader');
  const headerSection = header && header.closest('.shopify-section');
  if (hall && headerSection) {
    const setLift = () => {
      const lift = hall.getBoundingClientRect().top - headerSection.getBoundingClientRect().top;
      hall.style.setProperty('--bfcm-lift', `${Math.max(Math.round(lift), 0)}px`);
    };
    setLift();
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(setLift);
      observer.observe(headerSection);
      observer.observe(document.body);
    } else {
      window.addEventListener('resize', setLift);
    }
  }

  // The lit patch of wall behind the buying column ends with the offer box.
  // It reaches out up to 110 px on each side, but never over the print (or
  // the photo) and never past the edge of the screen.
  const column = product.querySelector('.product-column-right');
  const offer = product.querySelector('[data-bfcm-pdp-offer]');
  if (column && offer && 'ResizeObserver' in window) {
    const setWall = () => {
      const col = column.getBoundingClientRect();
      const picture = (printShown && print.querySelector('img')) || main;
      const room = col.left - picture.getBoundingClientRect().right - 8;
      const left = Math.max(Math.min(110, Math.round(room)), 24);
      const right = Math.max(Math.min(110, Math.round(document.documentElement.clientWidth - col.right - 4)), 0);
      column.style.setProperty('--bfcm-wall-h', `${Math.max(Math.round(offer.getBoundingClientRect().bottom - col.top), 0)}px`);
      column.style.setProperty('--bfcm-wall-l', `${left}px`);
      column.style.setProperty('--bfcm-wall-r', `${right}px`);
      column.classList.add('bfcm-wall-ready');
    };
    const observer = new ResizeObserver(setWall);
    observer.observe(column);
    observer.observe(gallery);
  }
})();
